import { advanceTenantEpoch, tenantEpoch } from '../api/tenant-context';
import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser, School } from '../types';
import { authApi, TokenPair } from '../api/auth.api';
import { schoolApi } from '../api/school.api';
import { resetCsrfToken } from '../api/csrf';
import { resetAdminMfaCancellation } from '../api/admin-mfa';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  user: AuthUser | null;
  school: School | null;
  availableSchools: School[];
  activeSchoolId: string | null;
  favoriteSchoolIds: string[];
  isBootstrapping: boolean;
  bootstrapFinished: boolean;
  bootstrap: () => Promise<void>;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  applySession: (payload: { user: AuthUser; tokens: TokenPair }) => Promise<void>;
  logout: () => Promise<void>;
  setActiveSchool: (schoolId: string) => Promise<void>;
  toggleFavoriteSchool: (schoolId: string) => void;
  refreshAccessibleSchools: () => Promise<void>;
}

const resolveInitialSchoolId = (profile: AuthUser | null): string | null => {
  if (!profile) {
    return null;
  }
  return (
    profile.schoolId ??
    profile.accessibleSchools?.[0]?.id ??
    null
  );
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      user: null,
      school: null,
      availableSchools: [],
      activeSchoolId: null,
      favoriteSchoolIds: [],
      isBootstrapping: true,
      bootstrapFinished: false,
      bootstrap: async () => {
        const { token } = get();
        if (!token) {
          set({ isBootstrapping: false, bootstrapFinished: true });
          return;
        }
        try {
          const profile = await authApi.fetchProfile();
          const activeSchoolId =
            resolveInitialSchoolId(profile) ?? null;
          const sorted = sortSchools(
            profile.accessibleSchools ?? [],
            get().favoriteSchoolIds,
          );
          set({
            user: profile,
            availableSchools: sorted,
            activeSchoolId,
            isBootstrapping: false,
            bootstrapFinished: true,
          });
          if (activeSchoolId) {
            await get().setActiveSchool(activeSchoolId);
          } else {
            set({ school: null });
          }
          await get().refreshAccessibleSchools();
        } catch (error) {
          console.warn('Bootstrap auth failed', error);
          if (!isAxiosError(error) || error.response?.status !== 401) {
            set({ isBootstrapping: false, bootstrapFinished: true });
            return;
          }
          set({
            token: null,
            refreshToken: null,
            tokenExpiresAt: null,
            user: null,
            school: null,
            availableSchools: [],
            activeSchoolId: null,
            isBootstrapping: false,
            bootstrapFinished: true,
          });
        }
      },
      login: async (credentials) => {
        const result = await authApi.login(credentials);
        await get().applySession(result);
      },
      applySession: async (payload) => {
        resetAdminMfaCancellation();
        const tokens: TokenPair = payload.tokens;
        const expiresAt =
          typeof tokens.expiresIn === 'number'
            ? Date.now() + tokens.expiresIn * 1000
            : null;
        const available = sortSchools(
          payload.user.accessibleSchools ?? [],
          get().favoriteSchoolIds,
        );
        const initialSchoolId = resolveInitialSchoolId(payload.user);
        set({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          tokenExpiresAt: expiresAt,
          user: payload.user,
          availableSchools: available,
          activeSchoolId: initialSchoolId,
          school: null,
          isBootstrapping: false,
          bootstrapFinished: true,
        });
        if (initialSchoolId) {
          await get().setActiveSchool(initialSchoolId);
        } else {
          set({ school: null });
        }
        await get().refreshAccessibleSchools();
      },
      logout: async () => {
        const { refreshToken, token } = get();
        // Effacer immédiatement : une réponse de refresh tardive ne doit pas réouvrir la session.
        set({ token: null, refreshToken: null, tokenExpiresAt: null, user: null, school: null,
          availableSchools: [], activeSchoolId: null, isBootstrapping: false, bootstrapFinished: true });
        resetCsrfToken();
        try { if (refreshToken) await authApi.logout(refreshToken, token); }
        catch (error) { console.warn('Remote logout failed', error); }
      },
      setActiveSchool: async (schoolId: string) => {
        const state = get();
        if (!schoolId) {
          set({ activeSchoolId: null, school: null });
          return;
        }
        if (state.activeSchoolId === schoolId && state.school) {
          return;
        }
        if (state.user?.role !== 'super_admin' && !state.availableSchools.some((school) => school.id === schoolId) && state.user?.schoolId !== schoolId) {
          throw new Error('Cette école ne fait pas partie de vos accès.');
        }
        set({ activeSchoolId: schoolId, school: null });
        const epoch = tenantEpoch();
        try {
          const school = await schoolApi.fetchCurrent();
          if (tenantEpoch() === epoch && get().activeSchoolId === schoolId) set({ school });
        } catch (error) {
          console.warn('Failed to load school context', error);
          if (tenantEpoch() === epoch) set({ school: null });
        }
      },
      toggleFavoriteSchool: (schoolId: string) => {
        if (!schoolId) {
          return;
        }
        set((state) => {
          const exists = state.favoriteSchoolIds.includes(schoolId);
          const favoriteSchoolIds = exists
            ? state.favoriteSchoolIds.filter((id) => id !== schoolId)
            : [...state.favoriteSchoolIds, schoolId];
          return {
            favoriteSchoolIds,
            availableSchools: sortSchools(
              state.availableSchools,
              favoriteSchoolIds,
            ),
          };
        });
      },
      refreshAccessibleSchools: async () => {
        try {
          const list = await schoolApi.fetchAccessibleSchools();
          set((state) => ({
            availableSchools: sortSchools(list, state.favoriteSchoolIds),
          }));
        } catch (error) {
          console.warn('Failed to refresh accessible schools', error);
        }
      },
    }),
    {
      name: 'pmd-pro-frontend-auth',
      // Version 1 : l'école gardée ici n'est plus que son résumé. Avant, l'API renvoyait l'école
      // complète (comptes avec empreintes de mot de passe, identifiants RdvPermis) et ce navigateur
      // la gardait telle quelle : la migration la réduit au résumé à la première ouverture.
      version: 1,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        return { ...state, school: toSchoolSummary(state.school) } as unknown as AuthState;
      },
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        user: state.user,
        school: toSchoolSummary(state.school),
        availableSchools: state.availableSchools,
        activeSchoolId: state.activeSchoolId,
        favoriteSchoolIds: state.favoriteSchoolIds,
      }),
    },
  ),
);

/** Seuls les champs que le logiciel lit (voir `School`) sont gardés dans le navigateur. */
function toSchoolSummary(value: unknown): School | null {
  if (!value || typeof value !== 'object') return null;
  const school = value as Record<string, unknown>;
  if (typeof school.id !== 'string' || typeof school.name !== 'string') return null;
  return {
    id: school.id,
    name: school.name,
    timezone: typeof school.timezone === 'string' ? school.timezone : 'Europe/Paris',
    agencyId: typeof school.agencyId === 'string' ? school.agencyId : null,
    agencyName: typeof school.agencyName === 'string' ? school.agencyName : null,
  };
}

function sortSchools(schools: School[], favoriteIds: string[]) {
  const favSet = new Set(favoriteIds);
  return [...schools].sort((a, b) => {
    const favScore = Number(favSet.has(b.id)) - Number(favSet.has(a.id));
    if (favScore !== 0) {
      return favScore;
    }
    return a.name.localeCompare(b.name);
  });
}

// Also catches access/profile refreshes, not only explicit school selections.
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id || state.user?.role !== previous.user?.role || state.activeSchoolId !== previous.activeSchoolId || Boolean(state.token) !== Boolean(previous.token)) advanceTenantEpoch();
});
